import type { Stripe, StripeCardElement } from "@stripe/stripe-js"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { AdminAccountCatalog, AdminAccountPayment } from "@ternary/contracts"
import {
  Badge,
  Button,
  breakable,
  Card,
  ConfirmDialog,
  DescriptionList,
  Dialog,
  Radio,
} from "@ternary/ui"
import { type FormEvent, useEffect, useId, useRef, useState } from "react"
import {
  AdminDiscountRefusedError,
  completeAdminAccountCard,
  confirmAdminPayment,
  confirmAdminPaymentSettled,
  createAdminAccountSetupIntent,
  createAdminAccountUpgradeSetupIntent,
  deleteAdminAccountCard,
  loadAdminAccount,
  requestAdminPasswordReset,
  restartAdminPayment,
  setDefaultAdminAccountCard,
  upgradeAdminAccount,
} from "../api.js"
import { FormLoading } from "../components/form-loading.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { cardOptions, PLAN_NAMES, PlanField } from "../components/setup-page.js"
import { SettingsHeader } from "./admin-settings-page.js"

let stripeScript: Promise<void> | undefined

function loadStripe() {
  if (window.Stripe) {
    return Promise.resolve()
  }
  stripeScript ??= new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.async = true
    script.src = "https://js.stripe.com/v3/"
    script.onload = () => resolve()
    script.onerror = () => reject(new Error("Stripe.js could not load."))
    document.head.append(script)
  })
  return stripeScript
}

type SavedCard = AdminAccountCatalog["cards"][number]

/**
 * Stripe's brand ids that are not a capitalised word. Stripe sends "unknown" for a brand it cannot
 * tell, which reads as a plain "Card".
 */
const BRAND_NAMES: Partial<Record<string, string>> = {
  amex: "American Express",
  cartes_bancaires: "Cartes Bancaires",
  diners: "Diners Club",
  eftpos_au: "eftpos Australia",
  interac: "Interac",
  jcb: "JCB",
  unionpay: "UnionPay",
  unknown: "Card",
}

/** A saved card, such as "Visa •••• 4242": its brand's name, then its last 4 digits. */
export function cardName(card: SavedCard) {
  const brand = Object.hasOwn(BRAND_NAMES, card.brand)
    ? BRAND_NAMES[card.brand]
    : card.brand.charAt(0).toUpperCase() + card.brand.slice(1)
  return `${brand} •••• ${card.last4}`
}

/**
 * Stripe's card field, mounted in `mount` once Stripe.js loads. `save` asks for a SetupIntent and
 * answers the payment method Stripe saved with it.
 */
function useCardField(catalog: AdminAccountCatalog) {
  const mount = useRef<HTMLDivElement>(null)
  const card = useRef<StripeCardElement | null>(null)
  const stripe = useRef<Stripe | null>(null)
  const [loadError, setLoadError] = useState("")

  useEffect(() => {
    if (catalog.provider !== "stripe" || !catalog.publishableKey) {
      return
    }
    let active = true
    void loadStripe()
      .then(() => {
        if (!active || !window.Stripe || !mount.current || !catalog.publishableKey) {
          return
        }
        const client = window.Stripe(catalog.publishableKey)
        stripe.current = client
        // The field's frame is Stripe's, so it takes the theme's colours where it sits.
        card.current = client.elements().create("card", cardOptions(mount.current))
        card.current.mount(mount.current)
      })
      .catch((reason) =>
        setLoadError(reason instanceof Error ? reason.message : "Stripe.js could not load."),
      )
    return () => {
      active = false
      card.current?.destroy()
    }
  }, [catalog.provider, catalog.publishableKey])

  async function save(setupIntent: () => Promise<{ clientSecret: string }>) {
    const client = stripe.current
    const element = card.current
    if (!client || !element) {
      throw new Error("Secure card entry is still loading.")
    }
    const setup = await setupIntent()
    const result = await client.confirmCardSetup(setup.clientSecret, {
      payment_method: { card: element },
    })
    if (result.error) {
      throw new Error(result.error.message ?? "Stripe could not save the card.")
    }
    const method = result.setupIntent?.payment_method
    const id = typeof method === "string" ? method : method?.id
    if (!id) {
      throw new Error("Stripe did not return the saved payment method.")
    }
    return id
  }

  /** P4: Stripe.js finishes the bank's check (3D Secure) on a first payment. */
  async function confirmPayment(clientSecret: string) {
    if (!stripe.current) {
      throw new Error("Secure card entry is still loading.")
    }
    const result = await stripe.current.confirmCardPayment(clientSecret)
    if (result.error) {
      throw new Error(result.error.message ?? "Your bank did not confirm the payment.")
    }
  }

  return { confirmPayment, loadError, mount, save }
}

/** Stripe's card field in a dialog. The card is saved only once Stripe confirms it. */
function CardEntryDialog({
  catalog,
  onClose,
  onSave,
}: {
  catalog: AdminAccountCatalog
  onClose(): void
  onSave(id: string): Promise<void> | void
}) {
  const formId = useId()
  const field = useCardField(catalog)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSaving(true)
    setError("")
    try {
      await onSave(await field.save(createAdminAccountSetupIntent))
    } catch (reason) {
      setSaving(false)
      setError(reason instanceof Error ? reason.message : "Card could not be added.")
    }
  }

  return (
    <Dialog
      footer={
        <>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button form={formId} loading={saving} type="submit">
            Add card
          </Button>
        </>
      }
      onClose={onClose}
      open
      title="Add card"
    >
      <form className="admin-stack" id={formId} onSubmit={submit}>
        <fieldset className="tny-field admin-card-field">
          <legend className="tny-label">Card details</legend>
          <div className="tny-input admin-card-input" ref={field.mount} />
        </fieldset>
        {error || field.loadError ? (
          <p className="tny-error" role="alert">
            {error || field.loadError}
          </p>
        ) : null}
      </form>
    </Dialog>
  )
}

/**
 * P3: Basic moves to a paid plan. The owner chooses the plan (never chosen for them), then the
 * card Stripe saves through the upgrade's SetupIntent pays for it.
 */
function UpgradeDialog({
  catalog,
  onClose,
  onUpgraded,
}: {
  catalog: AdminAccountCatalog
  onClose(): void
  onUpgraded(account: AdminAccountCatalog): void
}) {
  const formId = useId()
  const field = useCardField(catalog)
  const [priceId, setPriceId] = useState<string | null>(null)
  const [planError, setPlanError] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!priceId) {
      setPlanError("Choose a plan.")
      return
    }
    setSaving(true)
    setError("")
    try {
      const paymentMethodId = await field.save(createAdminAccountUpgradeSetupIntent)
      const upgrade = { paymentMethodId, priceId }
      let result = await upgradeAdminAccount(upgrade)
      // P4: the bank's check first, then the same request finds the payment done.
      if ("paymentAction" in result) {
        await field.confirmPayment(result.paymentAction.clientSecret)
        result = await upgradeAdminAccount(upgrade)
      }
      if ("paymentAction" in result) {
        throw new Error("Your bank has not confirmed the payment yet. Try again.")
      }
      onUpgraded(result)
    } catch (reason) {
      setSaving(false)
      setError(reason instanceof Error ? reason.message : "The plan could not be upgraded.")
    }
  }

  return (
    <Dialog
      description="A paid plan can sell paid products. The card pays for it from today."
      footer={
        <>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button form={formId} loading={saving} type="submit">
            Upgrade
          </Button>
        </>
      }
      onClose={onClose}
      open
      title="Upgrade your plan"
    >
      <form className="admin-stack" id={formId} onSubmit={submit}>
        <PlanField
          error={planError || undefined}
          name="upgrade-plan"
          onChange={(id) => {
            setPriceId(id)
            setPlanError("")
          }}
          plans={catalog.upgradePlans ?? []}
          value={priceId}
        />
        <fieldset className="tny-field admin-card-field">
          <legend className="tny-label">Card details</legend>
          <div className="tny-input admin-card-input" ref={field.mount} />
        </fieldset>
        {error || field.loadError ? (
          <p className="tny-error" role="alert">
            {error || field.loadError}
          </p>
        ) : null}
      </form>
    </Dialog>
  )
}

/**
 * The saved cards as radio cards: choose the default, add one, or remove one after a ConfirmDialog.
 * Errors show inside the dialog. Save default and the confirmed Remove send what they always sent.
 */
function PaymentMethodsDialog({
  catalog,
  error,
  onAdd,
  onClose,
  onDelete,
  onDefault,
  pending,
}: {
  catalog: AdminAccountCatalog
  error: string
  onAdd(): void
  onClose(): void
  onDelete(id: string): void
  onDefault(id: string): void
  pending: boolean
}) {
  const [selected, setSelected] = useState(
    catalog.cards.find((card) => card.default)?.id ?? catalog.cards[0]?.id ?? "",
  )
  const [removing, setRemoving] = useState<SavedCard | null>(null)
  const card = catalog.cards.find((candidate) => candidate.id === selected)

  return (
    <Dialog
      description="The default card pays for your Ternary subscription."
      footer={
        <>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button
            disabled={!catalog.writable || !card || card.default}
            loading={pending}
            onClick={() => card && onDefault(card.id)}
          >
            Save default
          </Button>
        </>
      }
      onClose={onClose}
      open
      title="Payment methods"
    >
      <div className="admin-stack">
        {catalog.cards.length > 0 ? (
          <div aria-label="Cards" className="admin-saved-cards" role="radiogroup">
            {catalog.cards.map((method) => (
              <div className="admin-saved-card" key={method.id}>
                <Radio
                  card
                  checked={selected === method.id}
                  label={
                    <>
                      {cardName(method)}
                      {method.default ? <Badge>Default</Badge> : null}
                    </>
                  }
                  name="admin-saved-card"
                  onChange={() => setSelected(method.id)}
                  value={method.id}
                />
                <Button
                  aria-label={`Remove ${cardName(method)}`}
                  disabled={!catalog.writable || pending}
                  onClick={() => setRemoving(method)}
                  size="sm"
                  variant="danger"
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="admin-note">No card yet.</p>
        )}
        <div className="admin-actions">
          <Button
            disabled={!catalog.writable || pending}
            onClick={onAdd}
            size="sm"
            variant="secondary"
          >
            Add card
          </Button>
        </div>
        {error ? (
          <p className="tny-error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
      <ConfirmDialog
        // Only the default card pays for the subscription, so only it leaves an invoice unpaid.
        body={
          removing?.default
            ? "This card pays for your subscription. Choose another default card so your next invoice can be paid."
            : "Your default card keeps paying for your subscription. You can add this card again later."
        }
        confirmLabel="Remove card"
        danger
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          if (removing) onDelete(removing.id)
          setRemoving(null)
        }}
        open={removing !== null}
        title={removing ? `Remove ${cardName(removing)}?` : ""}
      />
    </Dialog>
  )
}

/** A plan's name as registration offers it (Basic, Premium), or its tier for any other. */
function planName(tier: string) {
  return Object.hasOwn(PLAN_NAMES, tier)
    ? PLAN_NAMES[tier as keyof typeof PLAN_NAMES]
    : (tier[0]?.toUpperCase() ?? "") + tier.slice(1)
}

/**
 * P4-F1: a first payment left waiting on the bank's check (3D Secure). The server says it is
 * pending, so this is here after a reload too. It asks the server first, and runs Stripe.js only for
 * the check the server hands back; a failed check takes another card; a payment that can no longer
 * be finished starts the plan again.
 */
function FirstPaymentCard({ catalog, onPaid }: { catalog: AdminAccountCatalog; onPaid(): void }) {
  const [working, setWorking] = useState(false)
  const [error, setError] = useState("")
  const [failed, setFailed] = useState(false)
  const [restartable, setRestartable] = useState(false)
  // P3-M2: Stripe refused the sign-up discount; the owner may start at the full price.
  const [fullPrice, setFullPrice] = useState(false)
  const [showCard, setShowCard] = useState(false)

  async function bank() {
    if (catalog.provider !== "stripe" || !catalog.publishableKey) return null
    await loadStripe()
    return window.Stripe ? window.Stripe(catalog.publishableKey) : null
  }

  async function settle(answer: () => Promise<AdminAccountPayment>) {
    setWorking(true)
    setError("")
    try {
      let status = await answer()
      if (status.paymentAction) {
        const stripe = await bank()
        if (stripe) {
          const result = await stripe.confirmCardPayment(status.paymentAction.clientSecret)
          if (result.error) {
            setFailed(true)
            setError(result.error.message ?? "Your bank did not confirm the payment.")
            return
          }
        }
        status = await confirmAdminPaymentSettled()
      }
      if (status.active) {
        onPaid()
        return
      }
      if (status.restartable) {
        setRestartable(true)
        setError("This payment can no longer be finished. Start your plan again.")
        return
      }
      setError("Your bank has not confirmed the payment yet. Try again in a minute.")
    } catch (reason) {
      if (reason instanceof AdminDiscountRefusedError) setFullPrice(true)
      setError(reason instanceof Error ? reason.message : "The payment could not be finished.")
    } finally {
      setWorking(false)
    }
  }

  return (
    <Card title={catalog.pendingRenewal ? "Finish your payment" : "Finish your first payment"}>
      <div className="admin-stack">
        <p className="admin-note">
          {/* N-b: a renewal's payment puts the business live again; it is not its first. */}
          {catalog.pendingRenewal
            ? "Your bank asked to confirm your renewal. Finish your payment to put your business live again."
            : "Your bank asked to confirm your first payment. Your business goes live once it is paid."}
        </p>
        {error ? (
          <p className="tny-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="admin-actions">
          {restartable && fullPrice ? (
            <Button
              loading={working}
              onClick={() => void settle(() => restartAdminPayment({ withoutDiscount: true }))}
            >
              Start at the full price
            </Button>
          ) : restartable ? (
            <Button loading={working} onClick={() => void settle(() => restartAdminPayment())}>
              Start your plan again
            </Button>
          ) : (
            <Button loading={working} onClick={() => void settle(() => confirmAdminPayment())}>
              Finish payment
            </Button>
          )}
          {failed && !restartable ? (
            <Button disabled={working} onClick={() => setShowCard(true)} variant="secondary">
              Use another card
            </Button>
          ) : null}
        </div>
      </div>
      {showCard ? (
        <CardEntryDialog
          catalog={catalog}
          onClose={() => setShowCard(false)}
          onSave={async (paymentMethodId) => {
            setShowCard(false)
            await settle(() => confirmAdminPayment({ paymentMethodId }))
          }}
        />
      ) : null}
    </Card>
  )
}

function statusLabel(status: AdminAccountCatalog["status"]) {
  if (status === "cancelling") {
    return "Expires at the end of the period"
  }
  return status[0]?.toUpperCase() + status.slice(1).replace("_", " ")
}

const PERIODS = { day: "Daily", month: "Monthly", week: "Weekly", year: "Yearly" } as const

/**
 * How often the subscription bills: "Monthly", "Yearly", or "Every 3 months". A unit with no word
 * here, such as one a newer API sends, reads "Every <unit>" rather than nothing.
 */
export function periodLabel(catalog: AdminAccountCatalog) {
  const unit = catalog.interval
  return catalog.intervalCount === 1
    ? (PERIODS[unit] ?? `Every ${unit}`)
    : `Every ${catalog.intervalCount} ${unit}s`
}

export function AdminAccountPage() {
  const queryClient = useQueryClient()
  const query = useQuery({ queryFn: loadAdminAccount, queryKey: ["admin-account"] })
  const [showCards, setShowCards] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [showUpgrade, setShowUpgrade] = useState(false)
  const [notice, setNotice] = useState("")
  const [actionError, setActionError] = useState("")

  function receive(data: AdminAccountCatalog, message: string) {
    queryClient.setQueryData(["admin-account"], data)
    setActionError("")
    setNotice(message)
  }

  const complete = useMutation({
    mutationFn: completeAdminAccountCard,
    onSuccess(data) {
      setShowAdd(false)
      setShowCards(true)
      receive(data, "Card added successfully.")
    },
  })
  const makeDefault = useMutation({
    mutationFn: setDefaultAdminAccountCard,
    onSuccess(data) {
      setShowCards(false)
      receive(data, "Default card updated.")
    },
  })
  const remove = useMutation({
    mutationFn: deleteAdminAccountCard,
    async onSuccess() {
      setShowCards(false)
      setActionError("")
      setNotice("Card removed successfully.")
      await queryClient.invalidateQueries({ queryKey: ["admin-account"] })
    },
  })
  const resetPassword = useMutation({
    mutationFn: requestAdminPasswordReset,
    onSuccess(data) {
      setActionError("")
      setNotice(data.message)
    },
    onError(error) {
      setNotice("")
      setActionError(
        error instanceof Error ? error.message : "Password reset could not be requested.",
      )
    },
  })

  if (query.isPending || query.isError) {
    return (
      <section aria-busy={query.isPending || undefined} className="admin-stack admin-page">
        <SettingsHeader active="account" />
        {query.isPending ? (
          <FormLoading fields={4} label="Loading account…" />
        ) : (
          <LoadError
            detail={errorDetail(query.error) ?? "The billing provider did not respond."}
            message="Account billing could not load"
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
          />
        )}
      </section>
    )
  }

  const account = query.data
  const defaultCard = account.cards.find((card) => card.default)
  const money = new Intl.NumberFormat("en-US", {
    currency: account.currency,
    style: "currency",
  }).format(account.nextInvoiceMinor / 100)
  const cardsPending = makeDefault.isPending || remove.isPending
  // The dialog's own request failed: it shows there, because the page behind a modal is inert.
  const cardsError = [makeDefault.error, remove.error].find((error) => error instanceof Error)
  const tier = planName(account.tier)
  // P3: Basic (the free plan) pays for nothing, so it has no period, invoice or card: its upgrade.
  const basic = account.tier === "free"

  return (
    <section className="admin-stack admin-page">
      <SettingsHeader active="account" />
      {!account.writable ? (
        <p className="admin-note">
          Billing changes are unavailable until platform Stripe testing keys are configured.
        </p>
      ) : null}
      {actionError ? (
        <p className="tny-error" role="alert">
          {actionError}
        </p>
      ) : null}
      <p className="admin-note" role="status">
        {notice}
      </p>

      {account.firstPaymentPending ? (
        <FirstPaymentCard
          catalog={account}
          onPaid={() => {
            setNotice("Your payment is complete. Your business is live.")
            // The session, its banner and this page all read the business as active now.
            void queryClient.invalidateQueries()
          }}
        />
      ) : null}

      <Card
        actions={
          account.status === "active" ? (
            <Badge tone="success">{statusLabel(account.status)}</Badge>
          ) : (
            <Badge tone="danger">{statusLabel(account.status)}</Badge>
          )
        }
        title="My subscription"
      >
        <DescriptionList
          items={
            basic
              ? [
                  {
                    term: "Plan",
                    value: account.upgradePlans?.length ? (
                      <span className="admin-fact-action">
                        <span>{tier}</span>
                        <Button
                          disabled={!account.writable}
                          onClick={() => setShowUpgrade(true)}
                          size="sm"
                          variant="secondary"
                        >
                          Upgrade
                        </Button>
                      </span>
                    ) : (
                      tier
                    ),
                  },
                  { term: "Price", value: "Free. No transactional fee." },
                  { term: "Email", value: breakable(account.email) },
                ]
              : [
                  { term: "Plan", value: tier },
                  { term: "Email", value: breakable(account.email) },
                  { term: "Billing period", value: periodLabel(account) },
                  { term: "Next invoice", value: money },
                  {
                    term: "Renews",
                    value: account.renewsAt
                      ? new Date(account.renewsAt).toLocaleDateString("en-US", {
                          dateStyle: "medium",
                        })
                      : "—",
                  },
                  {
                    term: "Default card",
                    // The cards are managed beside the one that pays.
                    value: (
                      <span className="admin-fact-action">
                        <span>{defaultCard ? cardName(defaultCard) : "No card"}</span>
                        <Button
                          disabled={!account.writable}
                          onClick={() => {
                            makeDefault.reset()
                            remove.reset()
                            setShowCards(true)
                          }}
                          size="sm"
                          variant="secondary"
                        >
                          Payment methods
                        </Button>
                      </span>
                    ),
                  },
                ]
          }
        />
      </Card>

      {/* Its one action beside its title, so the page still fits a phone. */}
      <Card
        actions={
          <Button
            loading={resetPassword.isPending}
            onClick={() => resetPassword.mutate({ email: account.email })}
            size="sm"
            variant="secondary"
          >
            Reset password
          </Button>
        }
        title="Sign-in & security"
      >
        {null}
      </Card>

      {showCards ? (
        <PaymentMethodsDialog
          catalog={account}
          error={cardsError instanceof Error ? cardsError.message : ""}
          onAdd={() => {
            complete.reset()
            setShowCards(false)
            setShowAdd(true)
          }}
          onClose={() => setShowCards(false)}
          onDefault={(id) => makeDefault.mutate(id)}
          onDelete={(id) => remove.mutate(id)}
          pending={cardsPending}
        />
      ) : null}
      {showUpgrade ? (
        <UpgradeDialog
          catalog={account}
          onClose={() => setShowUpgrade(false)}
          onUpgraded={(data) => {
            setShowUpgrade(false)
            receive(data, `Your plan is now ${planName(data.tier)}.`)
          }}
        />
      ) : null}
      {showAdd ? (
        <CardEntryDialog
          catalog={account}
          onClose={() => {
            setShowAdd(false)
            setShowCards(true)
          }}
          onSave={(id) => complete.mutateAsync(id).then(() => undefined)}
        />
      ) : null}
    </section>
  )
}
